// src/App.js
//
// This one static build's real entry point: decides, at runtime, in the
// browser, which of THREE completely separate app trees to mount —
// TenantApp (the POS app itself, every business's own subdomain),
// PlatformApp (admin.posflect.com, Super Admin only), or MarketingApp
// (posflect.com/www.posflect.com, anonymous marketing visitors) — see
// isPlatformHost/isMarketingHost below for exactly how each host is
// recognized.
//
// All three are React.lazy() dynamic imports rather than plain top-level
// imports — this feature's own bundle-size audit's own top finding: with
// plain imports, a marketing visitor's browser downloaded the ENTIRE
// tenant POS app (TenantApp used to be ~1200 lines of routes + ~100 page
// component imports, right here in this file) plus the entire Platform
// admin panel, just to render a landing page, because a bundler inlines
// anything reachable via a plain `import`, whether or not it's ever
// actually rendered. TenantApp's own former body now lives in its own
// file, TenantApp.js (see that file's own doc comment) — the same
// "completely separate tree, its own file" shape PlatformApp.js and
// MarketingApp.js already had before this change, which is exactly what
// makes them trivial to lazy-load too: no file split needed for those two,
// just wrapping their existing default export in lazy(() => import(...)).
//
// NoIndexNonMarketingHost is deliberately kept OUTSIDE every <Suspense>
// boundary below, as a plain immediate sibling — it must set its
// <meta name="robots"> tag the instant App() itself renders, not delayed
// behind whichever lazy chunk is still downloading, since a crawler that
// gives up before that chunk resolves must still see the override.
import { lazy, Suspense, useEffect } from "react";

// See this file's own doc comment above for why these three are lazy
// rather than plain imports. TenantApp is the common case for anyone who
// isn't visiting admin.posflect.com or posflect.com directly, so its own
// Suspense fallback (AppLoadingFallback, below) is intentionally minimal —
// a returning tenant user's browser already has this chunk cached from
// their last visit in the overwhelming majority of cases anyway.
const TenantApp = lazy(() => import("./TenantApp"));
const PlatformApp = lazy(() => import("./PlatformApp"));
const MarketingApp = lazy(() => import("./pages/Marketing/MarketingApp"));

// isPlatformHost decides, at runtime, in the browser, which of this one
// static build's three completely separate app trees to mount — see
// PlatformApp.js's own doc comment on why that split exists at the
// component level rather than as one more branch inside TenantApp.js's
// own giant <Routes> tree (see this file's own top-of-file doc comment
// for why that tree now lives in its own file rather than here).
// process.env.REACT_APP_PLATFORM_HOST is a
// LOCAL-DEV override only (a dev machine has no real "admin.posflect.com"
// DNS entry to visit) — every real deployment instead matches this exact
// build's own runtime hostname's leading label against "admin", the same
// convention this feature's own plan doc and platform_routes.go's own doc
// comment already name (deploy/posflect-provision-subdomain.sh's ROOT_DOMAIN
// is what the wildcard DNS record and every company's own subdomain are
// built from; "admin" is simply never a company's own subdomain — see
// platform.ValidateSubdomainFormat's reserved-word blocklist).
const PLATFORM_HOST = process.env.REACT_APP_PLATFORM_HOST;
const isPlatformHost = PLATFORM_HOST
  ? window.location.hostname === PLATFORM_HOST
  : window.location.hostname.split(".")[0] === "admin";

// isMarketingHost is this feature's own third branch — the root domain
// itself, posflect.com (and its "www" alias), gets MarketingApp instead of
// silently falling into TenantApp and 400ing on every API call with
// TENANT_NOT_FOUND (see this feature's own plan doc's Grounding section).
// Deliberately an EXPLICIT hostname check, not a generic "fewer than 3
// dot-labels" mirror of the backend's own subdomain-extraction logic —
// that generic rule would also match bare "localhost", which would break
// every developer's existing local TenantApp dev workflow (none of them
// visit a real posflect.com in local dev). REACT_APP_MARKETING_HOST is a
// LOCAL-DEV override only, same convention as REACT_APP_PLATFORM_HOST just
// above. Checked only when isPlatformHost is already false — "admin" must
// always win if somehow both matched.
const MARKETING_HOST = process.env.REACT_APP_MARKETING_HOST;
const isMarketingHost = !isPlatformHost && (
  MARKETING_HOST
    ? window.location.hostname === MARKETING_HOST
    : window.location.hostname === "posflect.com" || window.location.hostname === "www.posflect.com"
);


// NoIndexNonMarketingHost is this feature's own SEO plan doc's other half —
// public/index.html's own static <meta name="robots" content="index,
// follow"> is written from posflect.com's own point of view (see that
// file's own comment on why), since that's the one host actually meant to
// be found. Every OTHER host this one build serves — admin.posflect.com,
// every tenant's own subdomain — must NOT be indexed: there's nothing on
// a tenant's own subdomain worth surfacing in search results, and it would
// only ever show a login screen or leak that a given business subdomain
// exists at all. A single static robots.txt can't tell hosts apart (same
// file is served on every one of them), so this is the actual per-host
// enforcement — flipped to "noindex, nofollow" here, at runtime, the
// moment this app knows which host it's actually running on. A crawler
// that doesn't execute JavaScript never sees this override and falls back
// to index.html's own "index, follow" default regardless of host; in
// practice that's a low real-world risk (nothing links to a random tenant
// subdomain from anywhere else on the web), and Google/Bing — the two
// crawlers that matter most — both execute this exactly like any other
// visitor's browser would.
function NoIndexNonMarketingHost() {
  useEffect(() => {
    if (isMarketingHost) return;
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    meta.content = "noindex, nofollow";
  }, []);

  return null;
}

// AppLoadingFallback — the Suspense fallback for whichever of the three
// lazy trees above is still downloading its own chunk. Deliberately
// styled inline (no external CSS import, no dependency on any of the
// three trees' own stylesheets — all of which are themselves part of the
// chunk this fallback exists to cover for) so it can render instantly with
// zero extra network requests, the same reasoning MarketingApp.js's own
// MarketingSuspenseFallback doc comment gives for reusing already-shipped
// CSS rather than adding a second one — this one goes a step further and
// ships no CSS of its own at all, since even that file's own "already
// shipped" CSS is itself behind a lazy boundary from this file's point of
// view.
function AppLoadingFallback() {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f6f7fb",
        color: "#4b5568",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontSize: 14,
        letterSpacing: "0.04em",
      }}
    >
      POSFLECT
    </div>
  );
}

export default function App() {
  if (isPlatformHost) {
    return (
      <>
        <NoIndexNonMarketingHost />
        <Suspense fallback={<AppLoadingFallback />}>
          <PlatformApp />
        </Suspense>
      </>
    );
  }
  if (isMarketingHost) {
    return (
      <Suspense fallback={<AppLoadingFallback />}>
        <MarketingApp />
      </Suspense>
    );
  }
  return (
    <>
      <NoIndexNonMarketingHost />
      <Suspense fallback={<AppLoadingFallback />}>
        <TenantApp />
      </Suspense>
    </>
  );
}
